import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SparklesIcon } from "lucide-react";
import posthog from "posthog-js";
import { useEffect, useState } from "react";

import ChatConversation from "./ChatConversation";
import ChatInput from "./ChatInput";
import ChatQuiz, { type QuizData, type QuizResults } from "./ChatQuiz";
import type { ChatController } from "~/hooks/useChatController";

interface ChatConversationPaneProps {
	controller: ChatController;
}

/** `label` is the bubble's text; `message` is what the learner sends when they pick it (the label when not set). */
const RECOMMENDED_PROMPTS: { label: string; message?: string }[] = [
	{ label: "Summarize this content" },
	{ label: "Explain key concepts" },
	{
		label: "Socratic conversation",
		message:
			"Can we have a Socratic conversation about this content? Ask me questions to see what I already know.",
	},
	{ label: "Create a practice quiz" },
	// {
	// 	label: "Flashcards",
	// 	message: "Can you make me some flashcards from this content?",
	// },
];

const ChatConversationPane = ({ controller }: ChatConversationPaneProps) => {
	const reduceMotion = useReducedMotion();
	const [activeQuiz, setActiveQuiz] = useState<QuizData | null>(null);

	const {
		activeChatThreadId,
		isLoading,
		chat,
		handleStatusChange,
		inputRef,
		draft,
		setDraft,
		handleSubmit,
		isSendDisabled,
		handleSendPrompt,
		handleSendQuizResult,
	} = controller;

	useEffect(() => {
		const handleOpenQuiz = (e: Event) => {
			const customEvent = e as CustomEvent;
			setActiveQuiz(customEvent.detail.quizData);
		};

		window.addEventListener("open-chat-quiz", handleOpenQuiz);
		return () => window.removeEventListener("open-chat-quiz", handleOpenQuiz);
	}, []);

	const handleQuizClose = (results?: QuizResults) => {
		setActiveQuiz(null);
		if (results) {
			handleSendQuizResult(results);
		}
	};

	return (
		<>
			{isLoading && (
				<div className="flex flex-1 items-center justify-center text-slate-400 text-sm">
					Loading…
				</div>
			)}

			{!isLoading && chat !== null && (
				<ChatConversation
					key={activeChatThreadId}
					chat={chat}
					onStatusChange={handleStatusChange}
				/>
			)}

			{!isLoading && activeChatThreadId === null && (
				<motion.div
					initial={reduceMotion ? false : { opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, ease: "easeOut" }}
					className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center"
				>
					<span className="flex size-12 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-500 text-white shadow-md">
						<SparklesIcon className="size-6" />
					</span>
					<div>
						<h2 className="font-bold text-lg text-slate-900 dark:text-white">
							How can I help?
						</h2>
						<p className="mt-0.5 text-slate-500 text-sm dark:text-slate-400">
							Ask about movement, anatomy, or anything in your courses.
						</p>
					</div>
					<div className="mt-2 flex max-w-sm flex-wrap justify-center gap-2">
						{RECOMMENDED_PROMPTS.map(({ label, message }) => (
							<button
								key={label}
								type="button"
								onClick={() => {
									posthog.capture("ai_tutor:quick_prompt_clicked", { label });
									handleSendPrompt(message ?? label);
								}}
								className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 font-medium text-slate-700 text-xs shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-slate-700 dark:hover:bg-slate-800"
							>
								{label}
							</button>
						))}
					</div>
				</motion.div>
			)}

			<ChatInput
				inputRef={inputRef}
				value={draft}
				onChange={setDraft}
				onSubmit={handleSubmit}
				disabled={isSendDisabled}
				placeholder={activeChatThreadId ? "Continue chat…" : "Ask AI…"}
			/>

			<AnimatePresence>
				{activeQuiz && <ChatQuiz quiz={activeQuiz} onClose={handleQuizClose} />}
			</AnimatePresence>
		</>
	);
};

export default ChatConversationPane;
